import withStyles from 'isomorphic-style-loader/withStyles';
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { intlShape, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import withWidth, {
  isWidthUp,
  WithWidthProps,
} from '@material-ui/core/withWidth/index';
import { convertToRaw, EditorState, Modifier } from 'draft-js';
import { stateFromHTML } from 'draft-js-import-html';
import { createEditorState, NOT_HANDLED } from 'medium-draft';
import objectHash from 'object-hash';
import mediumDraftStyles from 'medium-draft/dist/medium-draft.css';

import * as courseDetailsActions from '../../../actions/courseDetails';
import * as lessonDetailsActions from '../../../actions/lessonDetails';
import {
  convertRichEditorContentToHtml,
  convertFromDraftToPlain,
  getImagesFromDraftJS,
} from '../../../helpers';
import isMobileUserAgent from '../../../helpers/checkMobileUserAgent';
import isLoggedIn from '../../../helpers/checkAuth';

import Attachments from '../Attachments/Attachments';
import EmbeddedAttachments from '../EmbeddedAttachments/EmbeddedAttachments';
import LessonSources from '../LessonSources/LessonSources';

import s from './Text.scss';
import convertImages from './convertImages';
import decorators from './decorators';

import LessonTextContent from './LessonTextContent';

import messages from '../lessonDetailsMessages';

@withWidth()
@injectIntl
@withStyles(s)
@withStyles(mediumDraftStyles)
@connect(
  state => ({
    auth: state.auth,
    lesson: state.lessonDetails.lesson,
    course: state.courseDetails.course,
    updatingTextNotes: state.lessonDetails.updatingTextNotes,
    isNotesShown: state.lessonDetails.isNotesShown,
    loadingTextNotes: state.lessonDetails.loadingTextNotes,
    textNotes: state.lessonDetails.textNotes,
    offline: state.online.offline,
  }),
  dispatch =>
    bindActionCreators(
      {
        getLessonTextNotes: lessonDetailsActions.getLessonTextNotes,
        updateState: lessonDetailsActions.updateState,
        getCourseBySlug: courseDetailsActions.getCourseBySlug,
      },
      dispatch,
    ),
)
class Text extends React.Component {
  static propTypes = {
    complete: PropTypes.func.isRequired,
    lesson: PropTypes.shape({
      _id: PropTypes.string.isRequired,
    }).isRequired,
    course: PropTypes.shape({
      _id: PropTypes.string,
    }).isRequired,
    updateState: PropTypes.func.isRequired,
    updatingTextNotes: PropTypes.bool.isRequired,
    isNotesShown: PropTypes.bool.isRequired,
    intl: intlShape.isRequired,
    loadingTextNotes: PropTypes.bool.isRequired,
    contentTypeSwitchers: PropTypes.shape({
      video: PropTypes.bool.isRequired,
      audio: PropTypes.bool.isRequired,
      text: PropTypes.bool.isRequired,
    }).isRequired,
    textRef: PropTypes.func,
    className: PropTypes.string,
    offline: PropTypes.bool.isRequired,
    ...WithWidthProps,
  };

  static defaultProps = {
    textRef: () => null,
    className: '',
  };

  static contextTypes = {
    fetch: PropTypes.func,
    cloudfrontUrl: PropTypes.string,
  };

  previousTop = 0;

  constructor(props, context) {
    super(props);

    const {
      textNotes,
      auth,
      textRef,
      intl: { formatMessage },
      lesson,
    } = this.props;

    this.MIN_PAGE_Y_HIGHTLIGHT = isWidthUp('sm', this.props.width)
      ? screen.height - 300 // eslint-disable-line no-restricted-globals
      : 0.7 * screen.height; // eslint-disable-line no-restricted-globals

    this.updateTextNotesWithDebounce = _.debounce(this.updateTextNotes, 500);
    this.setStateDebounced = _.debounce(this.setState, 600);
    this.handleScrollEventOnMobileDebounced = _.debounce(
      this.handleScrollEventOnMobile,
      600,
    );

    const { content, sources } = props.lesson;
    const newContent = convertImages({ cloudfrontUrl: context.cloudfrontUrl })(
      content,
    );
    const newSources = _.cloneDeep(sources);

    if (isLoggedIn(auth)) {
      _.each(textNotes, ({ offset, length, blockKey }) => {
        const blockIndex = _.findIndex(content.blocks, { key: blockKey });
        if (blockIndex !== -1) {
          newContent.blocks[blockIndex].inlineStyleRanges.push({
            style: 'HIGHLIGHT',
            offset,
            length,
          });
        }
      });
    }

    this.state = {
      contentScrolledPercent: 0,
      rerenderEditor: false,
    };

    let editorState = createEditorState(newContent, decorators);
    const convertedSourcesData = convertRichEditorContentToHtml(
      newSources,
      context.cloudfrontUrl,
    );
    const editorStateContent = editorState && editorState.getCurrentContent();
    const imageContent = getImagesFromDraftJS(_.get(props, 'lesson.content'));

    const isTextExist =
      (editorStateContent &&
        editorStateContent.hasText() &&
        !!editorStateContent.getPlainText().trim().length) ||
      !!_.get(imageContent, 'length');

    let showEmptyContentMessage = false;
    if (
      !isTextExist &&
      _.get(lesson, 'audios.length') &&
      !lesson.youtubeVideoUrl
    ) {
      const { blockMap } = stateFromHTML(
        formatMessage(messages.emptyContentMessage),
      );
      const newState = Modifier.replaceWithFragment(
        editorState.getCurrentContent(),
        editorState.getSelection(),
        blockMap,
      );
      editorState = EditorState.push(editorState, newState, 'insert-fragment');
      showEmptyContentMessage = true;
    }

    this.state.editorState = editorState;
    this.state.isSourcesExist = convertedSourcesData.isTextExist;
    this.state.isTextExist = isTextExist;
    this.state.sourcesHTML = convertedSourcesData.contentHTML;
    this.state.showEmptyContentMessage = showEmptyContentMessage;

    textRef(this);
  }

  componentDidMount() {
    document.querySelector('#lesson-content').scrollTop = 0;
    window.addEventListener('scroll', this.handleScrollEventOnMobileDebounced);
  }

  componentDidUpdate() {
    this.checkAndUpdateHighlightBtnPosition();
    // const el = this.editor._editorNode.editorContainer;
    // this.editorElement =
    //   this.editorElement ||
    //   document.getElementsByClassName('md-RichEditor-root')[0];
    // if (this.editorElement) {
    //   console.log(this.editorElement);
    //   console.log(this.editorElement.innerHeight);
    //   console.log(this.editorElement.outerHeight);
    //   console.log(this.editorElement.height);
    //   this.editorElement.style.height = `${this.editorElement.clientHeight}px`;
    // }
  }

  componentWillUnmount() {
    window.removeEventListener(
      'scroll',
      this.handleScrollEventOnMobileDebounced,
    );
  }

  // componentWillReceiveProps(nextProps) {
  //   if (_.get(this, 'props.lesson._id') !== _.get(nextProps, 'lesson._id')) {
  //     const { content, sources } = nextProps.lesson;
  //
  //     const newSources = _.cloneDeep(sources);
  //     const newContent = _.cloneDeep(content);
  //
  //     if (isLoggedIn(nextProps.auth)) {
  //       _.each(nextProps.textNotes, ({ offset, length, blockKey }) => {
  //         const blockIndex = _.findIndex(content.blocks, { key: blockKey });
  //         if (blockIndex !== -1) {
  //           newContent.blocks[blockIndex].inlineStyleRanges.push({
  //             style: 'HIGHLIGHT',
  //             offset,
  //             length,
  //           });
  //         }
  //       });
  //     }
  //
  //     const editorState = createEditorState(newContent);
  //     const editorStateSources = createEditorState(newSources);
  //     const editorStateContentSources = editorStateSources && editorStateSources.getCurrentContent();
  //     const isSourcesExist = editorStateContentSources && editorStateContentSources.hasText() && !!editorStateContentSources.getPlainText().trim().length;
  //
  //     this.setState({
  //       editorState,
  //       sourcesHTML: stateToHTML(editorStateContentSources),
  //       isSourcesExist,
  //     });
  //
  //     if (document && document.querySelector('#lesson-content')) {
  //       document.querySelector('#lesson-content').scrollTop = 0;
  //     }
  //   }
  // }

  onContentScroll = ({ target }) => {
    const { lesson, complete } = this.props;
    const { contentScrolledPercent } = this.state;

    function getHeight(D) {
      return Math.max(D.scrollHeight, D.offsetHeight, D.clientHeight);
    }

    const newContentScrolledPercent =
      (target.scrollTop + target.offsetHeight) / getHeight(target);

    if (newContentScrolledPercent < contentScrolledPercent) {
      return;
    }

    this.setStateDebounced({
      contentScrolledPercent: newContentScrolledPercent,
    });

    if (newContentScrolledPercent > 0.9 && !lesson.isCompleted) {
      complete();
    }
  };

  onEditorChange = (editorState, callback) => {
    const { rerenderEditor } = this.state;
    const newEditorPlainText = convertFromDraftToPlain(editorState, ' ');
    const oldEditorPlainText = convertFromDraftToPlain(
      this.state.editorState,
      ' ',
    );
    if (newEditorPlainText !== oldEditorPlainText) {
      return this.setState({ rerenderEditor: !rerenderEditor }, () => {
        if (callback) {
          callback();
        }
      });
    }
    this.setState({ editorState }, () => {
      if (callback) {
        callback();
      }
    });
    return this.updateTextNotesWithDebounce(editorState);
  };

  onEditorDoubleClick = e => {
    if (e.pageY > this.MIN_PAGE_Y_HIGHTLIGHT) {
      document.getElementById('lesson-content').scrollTop += 60; // pageY + MIN_PAGE_Y_HIGHTLIGHT -100;
    }
  };

  getCssValueFromProp = prop => parseInt(prop.slice(0, prop.indexOf('.')), 10);

  handleScrollEventOnMobile = () => {
    const textEl = document.querySelector('#lesson-content');

    if (!textEl) {
      return;
    }

    if (textEl.clientHeight !== textEl.scrollHeight) {
      return;
    }

    const { lesson, complete } = this.props;
    const { contentScrolledPercent } = this.state;

    const h = document.documentElement;
    const b = document.body;
    const st = 'scrollTop';
    const sh = 'scrollHeight';

    const newContentScrolledPercent =
      (h[st] || b[st]) / ((h[sh] || b[sh]) - h.clientHeight);

    if (newContentScrolledPercent < contentScrolledPercent) {
      return;
    }

    this.setStateDebounced({
      contentScrolledPercent: newContentScrolledPercent,
    });

    if (newContentScrolledPercent > 0.9 && !lesson.isCompleted) {
      complete();
    }
  };

  checkAndUpdateHighlightBtnPosition = () => {
    const d = document.getElementsByClassName('md-editor-toolbar--isopen');
    if (d.length) {
      this.updateElement(d[0]);
    }
  };

  updateElement = el => {
    const currentTop = this.getCssValueFromProp(el.style.top); // parseInt(top.slice(0, top.indexOf('.')))

    if (this.previousTop !== currentTop) {
      // move element lower
      el.style.removeProperty('top');
      el.style.top = `${currentTop + 90}px`; // eslint-disable-line no-param-reassign

      const width = this.getCssValueFromProp(el.style.width);
      // Check and move if element gonna outside the screen
      if (this.getCssValueFromProp(el.style.left) < 0.35 * width) {
        el.style.removeProperty('margin-left');
        el.style['margin-left'] = `${width / 3}px`; // eslint-disable-line no-param-reassign
        el.classList.add(s.mdArrowLeft);
      } else {
        el.style.removeProperty('margin-left');
        el.classList.remove(s.mdArrowLeft);
        el.style['margin-left'] = `0`; // eslint-disable-line no-param-reassign
      }

      if (el.style.top !== `${this.previousTop}px`) {
        this.forceUpdate();
      }
      this.previousTop = currentTop + 90;
    }
  };

  updateTextNotes = editorState => {
    const {
      lesson,
      textNotes,
      getLessonTextNotes,
      course: { _id: courseId },
      updateState,
    } = this.props;
    const { isNotesShown } = this.props;

    if (!isNotesShown || !courseId) {
      return false;
    }

    const { blocks } = convertToRaw(editorState.getCurrentContent());

    const highlights = [];
    _.each(blocks, block => {
      const inlineStyleRanges = _.filter(
        block.inlineStyleRanges,
        inlineStyleRangesItem => inlineStyleRangesItem.style === 'HIGHLIGHT',
      );
      if (inlineStyleRanges && inlineStyleRanges.length) {
        _.each(inlineStyleRanges, inlineStyleRangesItem => {
          const { offset, length } = inlineStyleRangesItem;
          const text = block.text.substr(offset, length);
          let order = _.get(
            _.find(textNotes, { blockKey: block.key, text, offset, length }),
            'order',
          );

          if (!_.isNumber(order)) {
            const max = _.max(_.map(textNotes, 'order'));
            order = _.isNumber(max) ? max + 1 : 0;
          }

          highlights.push({
            offset,
            length,
            text,
            blockKey: block.key,
            order,
          });
        });
      }
    });

    if (
      objectHash(highlights) ===
      objectHash(_.map(textNotes, textNote => _.omit(textNote, ['_id'])))
    ) {
      return false;
    }

    updateState('updatingTextNotes', true);

    return this.context
      .fetch(`/api/courses/${courseId}/text-notes/${lesson._id}`, {
        method: 'PUT',
        body: JSON.stringify(highlights),
      })
      .then(res => {
        updateState('updatingTextNotes', false);

        if (res.ok) {
          getLessonTextNotes(courseId, lesson._id);
        }
      })
      .catch(error => {
        console.error(error);

        updateState('updatingTextNotes', false);
      });
  };

  /*  Uncomment this function and add to the onTouchStart in element to disable
    default "Edit" toolbar on IOS. Working bad on android.

    lastTappedTimestamp = Date.now();

    isFirstTap = true;

    onDoubleTap = e => {
      const MIN_TIME_DELTA_BETWEEN_TAPS = 200; // in ms
      // eslint-disable-line no-return-assign consistent-return
      if (this.isFirstTap === 0) {
        this.isFirstTap = false;
        return;
      }
      const now = Date.now();
      const tapDelta = now - this.lastTappedTimestamp;
      // const msg = `On double Click ${tapDelta}, times ${this.d}`
      // this.setState({ onDblTap: msg });
      if (tapDelta < MIN_TIME_DELTA_BETWEEN_TAPS) {
        // this.setState({ onDblTap: 'prevented ' + msg });
        e.preventDefault();
        e.target.click();
      }
      this.lastTappedTimestamp = now;
    };  */

  keyBinding = () => 'do-nothing';

  handleReturn = () => NOT_HANDLED;

  handleKeyCommand = () => false;

  render() {
    const {
      intl: { formatMessage },
      contentTypeSwitchers,
      updatingTextNotes,
      isNotesShown,
      loadingTextNotes,
      auth,
      course: { _id: courseId },
      className,
      offline,
    } = this.props;

    const {
      editorState,
      isSourcesExist,
      sourcesHTML,
      isTextExist,
      showEmptyContentMessage,
    } = this.state;

    const inlineButtons = [
      {
        label: formatMessage(messages.highlightLabel),
        style: 'HIGHLIGHT',
      },
    ];

    return (
      <LessonTextContent
        ref={e => {
          // eslint-disable-next-line react/no-unused-class-component-methods
          this.editor = e;
        }}
        editorState={editorState}
        editorEnabled={
          !showEmptyContentMessage &&
          !offline &&
          isNotesShown &&
          isLoggedIn(auth) &&
          !updatingTextNotes &&
          !loadingTextNotes &&
          !!courseId &&
          !isMobileUserAgent()
        }
        onEditorChange={this.onEditorChange}
        handleKeyCommand={this.handleKeyCommand}
        keyBindingFn={this.keyBinding}
        handleReturn={this.handleReturn}
        inlineButtons={inlineButtons}
        isTextExist={
          showEmptyContentMessage
            ? contentTypeSwitchers.audio === true
            : isTextExist
        }
        fullWidth={!contentTypeSwitchers.video || offline}
        className={className}
        onContentScroll={this.onContentScroll}
        onSelect={() => false}
        onEditorDoubleClick={this.onEditorDoubleClick}
        key={`Lesson-content-${this.state.rerenderEditor}`}
      >
        <EmbeddedAttachments withVideo={!!contentTypeSwitchers.video} />
        <LessonSources
          key="sourcescontent"
          s={s}
          headerTitle={formatMessage(messages.sourcesLabel)}
          isSourcesExist={contentTypeSwitchers.text && isSourcesExist}
          sourcesHTML={sourcesHTML}
        />
        <Attachments withVideo={!!contentTypeSwitchers.video} />
      </LessonTextContent>
    );
  }
}

export default React.forwardRef((props, ref) => (
  <Text {...props} textRef={ref} />
));
